import { useState } from 'react'
import { Collaboration, Pagination } from '../../../gql/types'
import {
  Button,
  ButtonColor,
  Pagination as PaginationComponent,
  StarRating,
  VideoZoom,
} from '../../../components'
import { Avatar } from '../../../elements'
import { VideoPlayer } from '../../../components/VideoPlayer'
import { ViewCreatorSliderPublic } from './../components/ViewCreatorSliderPublic'
import { ViewPortfolioSliderPublic } from './../components/ViewPortfolioSliderPublic'
import { ViewReviewsSliderPublic } from './../components/ViewReviewsSliderPublic'

interface IProps {
  collaborations: Collaboration[]
  pagination?: Pagination
  setPage(page: number): void
  onRefetch: () => void
  showFavorites: boolean
}

const TableHead = () => {
  return (
    <thead className='bg-white'>
      <tr>
        <th
          scope='col'
          className='py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-600 sm:pl-6'
        >
          Creator
        </th>

        <th
          scope='col'
          className='px-3 py-3.5 text-left text-sm font-semibold text-gray-600'
        >
          Reviews
        </th>

        <th
          scope='col'
          className='py-3.5 pl-3 pr-4 text-left text-sm font-semibold text-gray-600 sm:pr-6'
        />
      </tr>
    </thead>
  )
}

const ApplicantsList = ({
  collaborations,
  pagination,
  setPage,
  onRefetch,
  showFavorites,
}: IProps) => {
  const [isCreatorSliderOpen, setIsCreatorSliderOpen] = useState(false)
  const [isPortfolioSliderOpen, setIsPortfolioSliderOpen] = useState(false)
  const [isReviewsSliderOpen, setIsReviewsSliderOpen] = useState(false)
  const [selectedCollaboration, setSelectedCollaboration] = useState<
    Collaboration | undefined
  >(undefined)

  const onCollaborationAccepted = () => {
    setIsCreatorSliderOpen(false)
    setIsPortfolioSliderOpen(false)
    setIsReviewsSliderOpen(false)
    setSelectedCollaboration(undefined)
    onRefetch()
  }

  return (
    <>
      <div className='flex flex-col'>
        <div className='mx-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4'>
          {pagination?.numberOfHits === 0 && (
            <div className='col-span-full'>
              <p className='py-4 text-sm text-center'>
                {showFavorites
                  ? 'No shortlisted applicants. View all applicants using the button above.'
                  : `No applicants yet. Check back later.`}
              </p>
            </div>
          )}

          {collaborations?.map((collaboration) => (
            <div
              key={collaboration.id}
              className='bg-white shadow rounded-lg p-4 flex flex-col border border-gray-200'
            >
              <div className='flex justify-between items-center mb-2'>
                <div className='flex items-center'>
                  <Avatar
                    type='user'
                    src={collaboration.creator.avatar?.url}
                    size='sm'
                    alt={collaboration.creator.firstName}
                  />
                  <div className='ml-2'>
                    <div className='text-lg font-semibold'>
                      {collaboration.creator.firstName}
                    </div>
                  </div>
                </div>
                <div>
                  <div
                    className='cursor-pointer'
                    onClick={() => {
                      setSelectedCollaboration(collaboration)
                      setIsReviewsSliderOpen(true)
                    }}
                  >
                    <StarRating
                      ratingScore={
                        collaboration?.creator?.metadata?.ratingsScore
                      }
                      ratingCount={
                        collaboration?.creator?.metadata?.ratingsCount
                      }
                    />
                  </div>
                </div>
              </div>
              {collaboration.creator.introVideo &&
                collaboration.creator.introVideo.url && (
                  <div className='mb-2'>
                    <div className='relative isolate flex flex-col justify-end shadow-sm shadow-black/20 dark:shadow-white/20 overflow-hidden rounded-2xl bg-gray-900 aspect-[0.7]'>
                      <VideoZoom
                        src={collaboration.creator.introVideo.url}
                        thumbnailSrc={collaboration.creator.thumbnail?.url}
                        className='cursor-pointer rounded-xl w-full object-cover hover:opacity-75'
                      />
                    </div>
                  </div>
                )}
              <div className='grid grid-cols-2 gap-2 mt-2'>
                <Button
                  title='View applicant'
                  type='button'
                  colour={ButtonColor.primary}
                  onClick={() => {
                    setSelectedCollaboration(collaboration)
                    setIsCreatorSliderOpen(true)
                  }}
                />
                <Button
                  title='View portfolio'
                  type='button'
                  colour={ButtonColor.secondary}
                  onClick={() => {
                    setSelectedCollaboration(collaboration)
                    setIsPortfolioSliderOpen(true)
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* todo: if no shortlisted applicants, show message */}

        {pagination && (
          <div className='mt-4'>
            <PaginationComponent
              pagination={pagination}
              setPage={setPage}
              className='bg-white'
            />
          </div>
        )}
      </div>

      {selectedCollaboration && (
        <>
          <ViewCreatorSliderPublic
            open={isCreatorSliderOpen}
            setOpen={setIsCreatorSliderOpen}
            creator={selectedCollaboration.creator}
            collaboration={selectedCollaboration}
            onCollaborationAccepted={() => onCollaborationAccepted()}
          />
          <ViewPortfolioSliderPublic
            open={isPortfolioSliderOpen}
            setOpen={setIsPortfolioSliderOpen}
            creator={selectedCollaboration.creator}
            className='max-w-[75%]'
          />
          <ViewReviewsSliderPublic
            open={isReviewsSliderOpen}
            setOpen={setIsReviewsSliderOpen}
            creator={selectedCollaboration.creator}
            className='max-w-[75%]'
          />
        </>
      )}
    </>
  )
}

export { ApplicantsList }
